import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error("Error fetching post:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4A5D23]/30 border-t-[#4A5D23] rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F9F8F4] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-['Playfair_Display'] text-3xl text-[#2D2A26] mb-4">Post Not Found</h1>
          <Link to="/" className="text-[#4A5D23] hover:underline">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F4]" data-testid="blog-post-page">
      {/* Hero */}
      {post.image_url && (
        <div className="relative h-[40vh] md:h-[50vh]">
          <img 
            src={post.image_url} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2D2A26]/60 to-transparent" />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link 
            to="/#blog" 
            className="inline-flex items-center gap-2 text-[#4A5D23] hover:gap-3 transition-all mb-8"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Link>

          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#2D2A26] mb-4">
            {post.title}
          </h1>

          <div className="flex items-center gap-4 text-[#8F9E82] mb-8">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-[#5C5852] font-light mb-8 border-l-4 border-[#4A5D23] pl-4">
              {post.excerpt}
            </p>
            <div className="text-[#5C5852] leading-relaxed whitespace-pre-wrap">
              {post.content}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-[#E8E4D9] rounded-2xl p-8 text-center">
            <h3 className="font-['Playfair_Display'] text-2xl text-[#2D2A26] mb-4">
              Have Questions?
            </h3>
            <p className="text-[#5C5852] mb-6">
              I'm here to help you navigate this journey with compassion and care.
            </p>
            <Link
              to="/#contact"
              className="inline-block bg-[#4A5D23] text-white px-8 py-3 rounded-full hover:bg-[#3A4A1C] transition-colors"
              data-testid="blog-cta-contact"
            >
              Schedule a Consultation
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPostPage;
